import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

/**
 * Cabeçalho padrão de página.
 *
 * Antes cada tela resolvia o próprio cabeçalho: Clientes usava `<Title>` azul
 * solto, Contas a Receber usava um `<h2>` dentro do Paper e Pipeline colava o
 * título junto dos filtros. Passa a existir um só formato:
 *
 *   título (+ contagem) (+ subtítulo)        [ ações primárias ]
 *   ───────────────────────────────────────────────────────────
 *   [ filtros ]
 */
const useStyles = makeStyles((theme) => ({
  root: {
    flex: "none",
    // Sem padding próprio: o respiro da página é do MainContainer.
    backgroundColor: theme.palette.background.default,
  },
  topRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  titleBlock: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  titleLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  title: {
    color: theme.palette.text.primary,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  count: {
    flexShrink: 0,
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,0.14)"
        : theme.palette.grey[100],
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    paddingTop: theme.spacing(2),
  },
  rule: {
    marginTop: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const PageHeader = ({ title, count, subtitle, actions, filters, onBack, backLabel }) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.topRow}>
        <div className={classes.titleBlock}>
          <div className={classes.titleLine}>
            {/* Telas de detalhe (cliente, projeto) precisam de volta para a
                lista. Fica dentro do PageHeader para o alinhamento com o
                título ser um só, e não dois cabeçalhos parecidos. */}
            {onBack && (
              <Tooltip title={backLabel}>
                <IconButton size="small" onClick={onBack} aria-label={backLabel}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="h2" component="h1" className={classes.title}>
              {title}
            </Typography>
            {count !== null && count !== undefined && (
              <span className={classes.count}>{count}</span>
            )}
          </div>
          {subtitle && (
            <Typography variant="body2" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
        </div>
        {actions && <div className={classes.actions}>{actions}</div>}
      </div>

      {filters && <div className={classes.filters}>{filters}</div>}

      <div className={classes.rule} />
    </div>
  );
};

PageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  /** Total de registros, exibido como pílula ao lado do título. */
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subtitle: PropTypes.node,
  /** Ações primárias da página (botões à direita). */
  actions: PropTypes.node,
  /** Linha de filtros abaixo do título. */
  filters: PropTypes.node,
  /** Presente em telas de detalhe: mostra a seta de voltar antes do título. */
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
};

PageHeader.defaultProps = {
  count: undefined,
  subtitle: undefined,
  actions: undefined,
  filters: undefined,
  onBack: undefined,
  backLabel: "Voltar",
};

export default PageHeader;
